import { useState, useCallback, useMemo, useEffect } from "react";
import { makeFilterToolbarProps } from "@/components/UI/DrilldownList";
import { useDebounce } from "@/utils/customHooks";
import { useInFlightToggle } from "@/utils/customHooks/useInFlightToggle";
import { useListControls } from "@/utils/customHooks/useListControls";
import { isLocalSearchFilterType, useSearchMethod } from "@/components/inputs/AccordionMultiSelect/comps/OptionsLightbox/utils/useSearchMethod";
import { useGetOptionsByFilterType } from "../../../utils/useGetOptionsByFilterType";

/**
 * One options "level" for the doc-hierarchy drilldown.
 *
 * Bundles everything a single DrilldownList level needs for a given filter config:
 * the options fetch, search, list controls (select-all / clear-all / invert / sort /
 * show-selected-first, incl. the remote "widen the fetch then select all" flow),
 * the SearchActionsToolbar props, and the DrilldownList-shaped option/value arrays.
 *
 * FilterOptionsPanel calls this TWICE — once for the parent list and once for the
 * drilled-in child — so the two levels are symmetric instead of two hand-copied
 * blocks. `hasChildren` puts a drill arrow on each option row (parent level only).
 *
 * @returns {{
 *   lightboxConfig, compType, status, isFetchingNextPage, hasNextPage, observerRef,
 *   setEnableDataFetch, search, options, selectedValue, showSelectedFirst,
 *   toolbarProps, handleRowChange
 * }}
 */
export const useOptionsLevel = ({
  filterConfig,
  value = [],
  onChange,
  contentTypes = [],
  activeFilters = [],
  initEnableDataFetch = true,
  hasChildren = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectAllMode, setSelectAllMode] = useState(false);
  const debounce = useDebounce();
  const isLocalSearch = isLocalSearchFilterType(filterConfig);

  const {
    lightboxConfig,
    observerRef,
    isFetchingNextPage,
    hasNextPage,
    status,
    setEnableDataFetch,
  } = useGetOptionsByFilterType({
    initEnableDataFetch,
    filterConfig,
    contentTypes,
    activeFilters,
    searchTerm,
    disableLimit: isLocalSearch || selectAllMode,
  });

  const { search, filteredOptions, handleSearchChange } = useSearchMethod({
    filterConfig,
    lightboxConfig,
    onSearchChange: setSearchTerm,
    debounce,
  });

  const rawOptions = lightboxConfig?.props?.options || [];
  const displayOptions = (isLocalSearch && filteredOptions !== null) ? filteredOptions : rawOptions;

  const listCtl = useListControls({ items: displayOptions, value, onChange });

  // Remote select-all: the toolbar click flips selectAllMode, which widens the fetch
  // (disableLimit); once the full set has loaded we apply the selection and reset.
  useEffect(() => {
    if (!selectAllMode || status !== "success") return;
    listCtl.selectAll();
    setSelectAllMode(false);
  }, [selectAllMode, status, listCtl.selectAll]);

  const handleSelectAll = useCallback(() => {
    if (isLocalSearch) listCtl.selectAll();
    else setSelectAllMode(true);
  }, [isLocalSearch, listCtl.selectAll]);

  // Individual row clicks toggle through the in-flight batcher (handles drag-select);
  // bulk actions (select-all etc.) go straight through listCtl → onChange.
  const toggle = useInFlightToggle();
  const handleRowChange = useCallback(
    (option) => {
      const next = toggle({
        key: "_",
        fallbackValues: value,
        option,
        buildEntry: (o, id) => ({ value: id, display: o.display }),
      });
      onChange(next);
    },
    [value, onChange, toggle]
  );

  const options = useMemo(
    () =>
      listCtl.orderedItems.map((o) => ({
        _id: o.value,
        display: o.display,
        value: o.value,
        loading: o.loading,
        hasChildren,
      })),
    [listCtl.orderedItems, hasChildren]
  );

  const selectedValue = useMemo(
    () => (Array.isArray(value) ? value : []).map((v) => ({ _id: v.value, display: v.display, value: v.value })),
    [value]
  );

  const toolbarProps = makeFilterToolbarProps(listCtl, {
    searchValue: search,
    onSearch: handleSearchChange,
    selectAll: {
      display: selectAllMode ? "Loading..." : "Select all",
      onClick: handleSelectAll,
      icon: "CheckCircle",
    },
  });

  return {
    lightboxConfig,
    compType: lightboxConfig?.comp,
    status,
    isFetchingNextPage,
    hasNextPage,
    observerRef,
    setEnableDataFetch,
    search,
    options,
    selectedValue,
    showSelectedFirst: listCtl.showSelectedFirst,
    toolbarProps,
    handleRowChange,
  };
};
